import { cn, IconButton, Image, PressEvent, TmdxAvatar } from '@ds';
import { RevertUrl } from '@icons';
import { ConditionalRender } from '@ui';
import { AssignedUsersByGroup, CaseResourceCombined, CaseResourceType, OrganType } from '@/api/apis/case';
import { EmployeeRole } from '@/api/apis/resources';
import { CASE_MANAGEMENT_GROUP_WEIGHT } from '@/constants/case/case.constants';
import { ResourceCard } from './ResourceCard';
import { getManagementTeam, getResourcesTeam } from './utils';
import { DeviceCard } from './DeviceCard';
import { MiniCard } from '../Base/MiniCard';
import { getColorsByOrganType } from '@/utils/helpers';
import { specialistCssStyles, SpecialistStylesStyle } from '../Base/specialistStyles';

type ResourcesListProps = {
  donorId: string;
  onFlip: (e: PressEvent) => void;
  resources: CaseResourceCombined[];
  assignedEmployees: AssignedUsersByGroup;
  organType: OrganType;
  isScs: boolean;
};

export const ResourcesList = ({
  donorId,
  onFlip,
  resources,
  assignedEmployees,
  organType,
  isScs,
}: ResourcesListProps) => {
  const resourcesTeam = getResourcesTeam(resources);
  const managementTeam = getManagementTeam(assignedEmployees, CASE_MANAGEMENT_GROUP_WEIGHT);
  const devices = resources.filter((r) => r.resourceType === CaseResourceType.OCSConsole);
  const hasTeam = !!(resourcesTeam.length + managementTeam.length + devices.length);
  const colors = getColorsByOrganType(organType);

  return (
    // Element should not be interactive, we just need to stop opening case details on resource list click
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={(e) => e.stopPropagation()}
      className={`w-full flex flex-col overflow-hidden scroll no-track ${organType}`}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="text-l-sb">{donorId}</div>
        <IconButton
          onPress={onFlip}
          variant="light"
          size="sm"
          className="focus:outline-none mr-0.5 min-h-6"
          startContent={<Image src={RevertUrl} alt="Return to card view" />}
        />
      </div>

      <div className="flex flex-col gap-2 overflow-y-auto">
        <ConditionalRender show={hasTeam} placeholder="No TMDX employees assigned">
          {resourcesTeam.map((r) =>
            r.employeeId ? (
              <ResourceCard
                employeeId={r.employeeId}
                key={`${r.id}_${r.roleOnCase}`}
                photo={r.photo}
                role={r.roleOnCase as EmployeeRole}
                name={r.name}
                organType={organType}
                labels={r.labels}
              />
            ) : null,
          )}
          {devices.map((d) => (
            <DeviceCard
              key={d.resourceId}
              organType={organType}
              deviceId={d.resourceId}
              avatarProps={{
                radius: 'lg',
              }}
            />
          ))}
          {isScs && (
            <MiniCard
              classNames={colors.bg80}
              label="Static cold storage"
              leftItem={<TmdxAvatar className={cn(colors.bg60, 'mr-2 w-8 h-8')} type="scs" size="md" radius="lg" />}
              rightItem={null}
              cssStyles={specialistCssStyles[SpecialistStylesStyle.base]}
            />
          )}
          {managementTeam.map((u) =>
            u.employeeId ? (
              <ResourceCard
                employeeId={u.employeeId}
                key={`${u.id}_${u.roleOnCase}`}
                photo={u.photo}
                role={u.roleOnCase as EmployeeRole}
                name={u.name}
                organType={organType}
                labels={u.labels}
              />
            ) : null,
          )}
        </ConditionalRender>
      </div>
    </div>
  );
};
